import { useLocalStorage } from '@vueuse/core';
import { useQueryStringRemover } from '~/composables/useQueryStringRemover';
import { flash } from '~/composables/flash';

export default defineNuxtRouteMiddleware(async (to) => {
  const query = to.query;
  const toast = useToast();
  const { status, data: session, token } = useAuth();
  const { isGranted } = useIsGranted();

  const routeIsProtected = to?.meta?.auth !== false; //globalAppMiddleware: true sets all pages to protected and to?.meta?.auth is undefined for those protected pages

  if (routeIsProtected && status.value === 'loading') {
    // avoid that the user get redirected to login while status is "loading"
    while (status.value === 'loading') {
      console.log('Catched a page change during auth status "loading"');
      await new Promise((resolve) => {
        return setTimeout(resolve, 100);
      });
    }
  }

  // Maintenance mode: redirect non-SUPER_ADMIN users away from protected routes
  const isSuperAdmin = session?.value?.roles?.includes('ROLE_SUPER_ADMIN');
  if (session.value?.maintenanceMode && !isSuperAdmin && routeIsProtected && to.path !== '/maintenance') {
    return navigateTo('/maintenance');
  }

  // Role-based access control
  const requiredRoles = to.meta.requiredRoles as string[] | undefined;
  if (requiredRoles && requiredRoles.length > 0 && status.value === 'authenticated') {
    const userRoles = session?.value?.roles ?? [];
    const hasRequiredRole = requiredRoles.some((role) => isGranted(role));

    if (!hasRequiredRole) {
      const isOrgUserOnly = userRoles.includes('ROLE_ORG_SCANNER') && !userRoles.includes('ROLE_ORG_ADMIN');
      const redirectTarget = isOrgUserOnly ? '/review-booster/scanner' : '/';
      return navigateTo(redirectTarget);
    }
  }

  flash('clear');

  const nuxtApp = useNuxtApp();
  const t = nuxtApp.$i18n.t;

  if (query.redirected === 'stripe' && query.session_id) {
    useQueryStringRemover().removeAll();
    try {
      const response = await $fetch<{ data: { status: string } }>(`/api/shop/stripe/session/${query.session_id}`, {
        baseURL: useRuntimeConfig().public.baseURL,
        headers: { 'Api-Authorization': token.value ?? '' },
      });
      if (response?.data?.status === 'complete') {
        useLocalStorage('cart', { items: [] }).value = { items: [] };
        toast.add({
          title: t('checkout.redirect.success.title'),
          description: t('checkout.redirect.success.description'),
          color: 'success',
          duration: 20_000,
        });
      } else {
        toast.add({
          title: t('checkout.redirect.cancelled.title'),
          description: t('checkout.redirect.cancelled.description'),
          color: 'warning',
          duration: 20_000,
        });
      }
    } catch {
      toast.add({ title: t('form.error'), description: t('cart.error'), color: 'error' });
    }
  }

  // Fallback: /admin/* requires ROLE_SUPER_ADMIN
  if (to.path.startsWith('/admin') && !session?.value?.roles?.includes('ROLE_SUPER_ADMIN')) {
    return abortNavigation({ statusCode: 404, message: t('request.error.404') });
  }

  if (status.value !== 'authenticated' && routeIsProtected) {
    const redirectAfterLogin = useLocalStorage('redirectAfterLogin', '/');
    redirectAfterLogin.value = to.fullPath;
  }

  /**
   * google e.g. redirects to opebo after oauth.
   * this request is answered by the api which then redirects to the frontend with a query param that should be translated to a toast or flash message
   */
  switch (query.rmsg) {
    case 'google.auth.success':
      toast.add({
        title: t('google.auth.success.title'),
        description: t('google.auth.success.details'),
        icon: 'heroicons:check-circle',
      });
      useQueryStringRemover().removeParam('rmsg');
      break;
    case 'google.auth.rejected':
    case 'google.auth.error':
    case 'google.auth.missing_scopes':
      toast.add({
        title: t(query.rmsg + '.title'),
        description: t(query.rmsg + '.details'),
        icon: 'heroicons:x-mark',
      });
      useQueryStringRemover().removeParam('rmsg');
      break;
  }
});
